import { motion } from "framer-motion";
import { Shield, Lock, Eye, Database, FileText, AlertCircle } from "lucide-react";

export function Privacy() {
  return (
    <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-primary/20 mb-6">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">Privacy Policy</span>
          </div>
          <h1 className="text-4xl sm:text-5xl mb-6">
            Your Privacy is
            <span className="block text-primary">Our Priority</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Last updated: October 10, 2025
          </p>
        </motion.div>

        {/* Quick Overview */}
        <motion.div 
          className="grid md:grid-cols-3 gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="p-6 rounded-2xl bg-card border border-border">
            <Lock className="w-8 h-8 text-primary mb-4" />
            <h3 className="mb-2">Cyber Essentials Aligned</h3>
            <p className="text-sm text-muted-foreground">UK government-backed cybersecurity for your health data</p>
          </div>
          <div className="p-6 rounded-2xl bg-card border border-border">
            <Eye className="w-8 h-8 text-primary mb-4" />
            <h3 className="mb-2">You Control Your Data</h3>
            <p className="text-sm text-muted-foreground">Choose what to share and with whom</p>
          </div>
          <div className="p-6 rounded-2xl bg-card border border-border">
            <Database className="w-8 h-8 text-primary mb-4" />
            <h3 className="mb-2">No Data Selling</h3>
            <p className="text-sm text-muted-foreground">We never sell your information to third parties</p>
          </div>
        </motion.div>

        {/* Privacy Content */}
        <motion.div 
          className="prose prose-lg max-w-none space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <FileText className="w-6 h-6 text-primary" />
              Introduction
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to CannaBias ("we," "our," or "us"). We are committed to protecting your privacy and handling your personal information with care. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              By using CannaBias, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Information We Collect</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl">Personal Information</h3>
              <p className="text-muted-foreground leading-relaxed">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="space-y-2 text-muted-foreground ml-6">
                <li>• <strong>Account Information:</strong> Email address, username, and profile information</li>
                <li>• <strong>Health Information:</strong> Cannabis usage logs, wellness tracking data, symptoms, and effects (sensitive personal data under UK GDPR)</li>
                <li>• <strong>Communication Data:</strong> Messages, posts, and interactions within our community</li>
                <li>• <strong>Payment Information:</strong> Billing details for premium services (processed securely through third-party payment processors)</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl">Automatically Collected Information</h3>
              <ul className="space-y-2 text-muted-foreground ml-6">
                <li>• <strong>Device Information:</strong> IP address, browser type, operating system</li>
                <li>• <strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
                <li>• <strong>Cookies and Tracking:</strong> We use cookies to enhance your experience and analyze platform usage</li>
              </ul>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Provide, maintain, and improve our services</li>
              <li>• Personalize your experience and provide tailored recommendations</li>
              <li>• Communicate with you about updates, features, and support</li>
              <li>• Ensure platform security and prevent fraud</li>
              <li>• Analyze usage patterns to improve our platform</li>
              <li>• Comply with legal obligations and enforce our terms</li>
              <li>• Connect you with verified healthcare professionals (with your consent)</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Cyber Essentials Alignment</h2>
            <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20">
              <p className="text-muted-foreground leading-relaxed mb-4">
                CannaBias aligns with the requirements of Cyber Essentials, the UK government-backed cybersecurity standards. Your health information is considered sensitive personal data under UK GDPR and is subject to strict security and privacy protections:
              </p>
              <ul className="space-y-2 text-muted-foreground ml-6">
                <li>• All sensitive data is encrypted both in transit (256-bit SSL) and at rest (AES-256)</li>
                <li>• Access to personal data is strictly limited to authorised personnel on a need-to-know basis</li>
                <li>• We maintain detailed audit logs of all data access</li>
                <li>• Regular security assessments and vulnerability testing are conducted</li>
                <li>• Data Processing Agreements (DPAs) are in place with all third-party service providers</li>
                <li>• Compliance with the five technical controls of Cyber Essentials</li>
              </ul>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Information Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell your personal information. We may share your information only in the following circumstances:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• <strong>With Your Consent:</strong> We share information when you explicitly authorize us to do so</li>
              <li>• <strong>Healthcare Providers:</strong> With verified healthcare professionals you choose to consult (with your explicit consent)</li>
              <li>• <strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our platform (under strict confidentiality agreements)</li>
              <li>• <strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li>• <strong>Safety and Security:</strong> To protect the rights, property, or safety of CannaBias, our users, or others</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Your Rights and Choices</h2>
            <p className="text-muted-foreground leading-relaxed">
              You have the following rights regarding your personal information:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• <strong>Access:</strong> Request a copy of your personal information</li>
              <li>• <strong>Correction:</strong> Request correction of inaccurate information</li>
              <li>• <strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li>• <strong>Portability:</strong> Request a machine-readable copy of your data</li>
              <li>• <strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li>• <strong>Restrict Processing:</strong> Request limitation on how we use your information</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              To exercise these rights, contact us at privacy@cannabias.co.uk
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will permanently delete your personal data within 30 days, except where we are required by law to retain certain information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Security Measures</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• End-to-end encryption for all data transmission</li>
              <li>• Regular security audits and vulnerability assessments</li>
              <li>• Multi-factor authentication options</li>
              <li>• Secure data centers with physical access controls</li>
              <li>• Employee training on data protection and privacy</li>
              <li>• Incident response and breach notification procedures</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              CannaBias is not intended for users under the age of 21 (or the legal age in their jurisdiction). We do not knowingly collect personal information from anyone under this age. If we become aware that we have collected information from a user under the required age, we will promptly delete it.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. We ensure that any such transfers comply with applicable data protection laws and that your information receives adequate protection.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="p-6 rounded-2xl bg-muted/30 border border-border">
              <p className="text-muted-foreground mb-2"><strong>Email:</strong> privacy@cannabias.co.uk</p>
              <p className="text-muted-foreground mb-2"><strong>Mail:</strong> CannaBias Privacy Team</p>
              <p className="text-muted-foreground mb-2">United Kingdom</p>
            </div>
          </section>

          <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg mb-2">Important Notice</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This privacy policy does not apply to third-party websites or services that may be linked from our platform. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
